<template>
  <v-container fluid class="pa-4 pa-md-6 d-flex flex-column" style="height: 100%;">
    <div class="mb-4 d-flex align-center">
      <div>
        <h2 class="text-h5">Assistente de desempenho</h2>
        <div class="text-medium-emphasis">
          Tire dúvidas sobre {{ isAluno ? "suas notas e faltas" : "o desempenho das suas turmas" }}.
        </div>
      </div>
      <v-spacer />
      <v-tooltip location="bottom" text="Salvar conversa em PDF">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            stacked
            size="small"
            variant="text"
            :disabled="mensagens.length === 0"
            :loading="exportando"
            @click="exportarPDF"
          >
            <v-icon icon="mdi-file-pdf-box" />
            Salvar
          </v-btn>
        </template>
      </v-tooltip>
    </div>

    <v-card rounded="lg" class="d-flex flex-column flex-grow-1" style="min-height: 0;">
      <div
        ref="mensagensEl"
        class="chat-mensagens pa-4"
        :class="exportando ? 'chat-mensagens--exportando' : 'flex-grow-1'"
      >
        <div v-show="exportando" class="pdf-cabecalho text-center mb-6">
          <img ref="logoPdf" src="@/assets/img/trackly-logo.png" alt="Trackly" height="70" />
          <div class="text-subtitle-1 font-weight-medium mt-2">
            Sistema Integrado de Gestão de Desempenho Acadêmico
          </div>
        </div>

        <v-alert
          v-if="mensagens.length === 0"
          type="info"
          variant="tonal"
          text="Pergunte algo como &quot;como está minha média nesse período?&quot; ou &quot;quais alunos estão com faltas altas?&quot;."
        />

        <div
          v-for="(mensagem, index) in mensagens"
          :key="index"
          class="d-flex mb-3"
          :class="mensagem.autor === 'usuario' ? 'justify-end' : 'justify-start'"
        >
          <v-card
            :color="mensagem.autor === 'usuario' ? 'primary' : undefined"
            :variant="mensagem.autor === 'usuario' ? 'flat' : 'tonal'"
            :class="mensagem.autor === 'usuario' ? 'text-white' : undefined"
            class="chat-bolha pa-3"
            rounded="lg"
          >
            <div class="chat-texto">{{ mensagem.texto }}</div>
          </v-card>
        </div>

        <div v-if="enviando" class="d-flex justify-start mb-3">
          <v-card variant="tonal" class="chat-bolha pa-3" rounded="lg">
            <v-progress-circular indeterminate size="18" width="2" />
          </v-card>
        </div>
      </div>

      <v-divider />

      <v-form class="pa-3 d-flex align-end" @submit.prevent="enviar">
        <v-textarea
          v-model="pergunta"
          placeholder="Digite sua pergunta..."
          variant="outlined"
          rows="1"
          auto-grow
          max-rows="4"
          hide-details
          density="comfortable"
          maxlength="500"
          :disabled="enviando"
          @keydown.enter.exact.prevent="enviar"
        />
        <v-btn
          class="ml-2"
          color="primary"
          icon="mdi-send"
          :loading="enviando"
          :disabled="!pergunta.trim()"
          @click="enviar"
        />
      </v-form>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor">
      {{ snackbarText }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { nextTick } from "vue";
import html2pdf from "html2pdf.js";
import store from "@/store";
import AssistenteDataService from "@/assets/js/services/AssistenteDataService";

export default {
  name: "Assistente",

  data: () => ({
    mensagens: [],
    pergunta: "",
    enviando: false,
    exportando: false,
    snackbar: false,
    snackbarColor: "error",
    snackbarText: "",
  }),

  computed: {
    isAluno() {
      return store.state.userType === "aluno";
    },
  },

  methods: {
    notify(text, color = "error") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    async rolarParaBaixo() {
      await nextTick();
      const el = this.$refs.mensagensEl;
      if (el) el.scrollTop = el.scrollHeight;
    },
    async enviar() {
      const texto = this.pergunta.trim();
      if (!texto || this.enviando) return;

      this.mensagens.push({ autor: "usuario", texto });
      this.pergunta = "";
      this.enviando = true;
      await this.rolarParaBaixo();

      try {
        const response = await AssistenteDataService.chat(texto);
        this.mensagens.push({ autor: "assistente", texto: response.data.resposta });
      } catch (error) {
        const detail = error.response?.data?.detail;
        this.notify(detail || "Não foi possível obter uma resposta do assistente. Tente novamente.");
      } finally {
        this.enviando = false;
        await this.rolarParaBaixo();
      }
    },
    async exportarPDF() {
      // Tira temporariamente o limite de altura/scroll do container para o
      // html2pdf capturar a conversa inteira, nao so o trecho visivel na tela.
      this.exportando = true;
      await nextTick();

      const logo = this.$refs.logoPdf;
      if (logo && !logo.complete) {
        await new Promise((resolve) => {
          logo.addEventListener("load", resolve, { once: true });
          logo.addEventListener("error", resolve, { once: true });
        });
      }

      const dataHora = new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date());

      try {
        await html2pdf(this.$refs.mensagensEl, {
          margin: 0.5,
          filename: `assistente-desempenho-${dataHora.replace(/[/:\s]/g, "-")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        });
      } catch (error) {
        this.notify("Não foi possível gerar o PDF.");
      } finally {
        this.exportando = false;
      }
    },
  },
};
</script>

<style scoped>
.chat-mensagens {
  overflow-y: auto;
}

.chat-mensagens--exportando {
  overflow: visible;
  max-height: none;
  height: auto;
}

.chat-bolha {
  max-width: 75%;
}

.chat-texto {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
